import { lazyMarkAsyncItemRead } from "owa-copilot-async-chat";
import { getAsyncItemEntry, consumeNotificationVisibleMs, getTimeInStatusMs, } from "owa-copilot-async-chat-store";
import { lazyOpenBizChatSidePane } from "owa-bizchat-shared";
import { logCoreUsage } from "owa-analytics";
import type { MailboxInfo } from "owa-client-types";
import type { AsyncChatOpenSource } from "./AsyncChatOpenSource";

export interface OpenAsyncConversationOptions {
    /** Registry entry id (= the provider's conversation id) to load in the sidepane. */
    conversationId: string;
    mailboxInfo: MailboxInfo;
    targetWindow: Window;
    /** Telemetry entry point for the sidepane open (the surface the user opened from). */
    source: AsyncChatOpenSource;
}

/**
 * Single source of truth for the "open an async conversation" handoff, shared by the DAB card stack
 * and the Tasks Hub. Marks the conversation read (optimistic local flip + service persist via
 * `markAsyncItemRead`) and loads it into the BizChat side pane.
 *
 * Lives in this non-UI package (not the React `owa-copilot-async-chat-ui` package) so eager
 * consumers like the chat drawer can invoke the handoff without pulling in the card UI bundle.
 * `lazyMarkAsyncItemRead` keeps the read-state persist code out of the eager bundle until first use
 * and routes to the provider that produced the entry.
 *
 * Callers layer their own surface-specific steps around this: the DAB dismisses the card from its
 * stack; the drawer flips out of the Tasks Hub view and invalidates the stale post-message handler
 * before calling this. Each caller passes the `source` matching the surface it opened from, which
 * is restricted to `AsyncChatOpenSource`, the surfaces onboarded onto the async registry.
 */
export function openAsyncConversation({ conversationId, mailboxInfo, targetWindow, source, }: OpenAsyncConversationOptions): void {
    const entry = getAsyncItemEntry(mailboxInfo, conversationId);
    
    logCoreUsage("AsyncChat_ConversationOpened", mailboxInfo, {
        source,
        status: entry?.status,
        kind: entry?.kind,
        conversationId,
        notificationVisibleMs: consumeNotificationVisibleMs(conversationId),
        timeInStatusMs: getTimeInStatusMs(entry),
        itemSource: entry?.source,
    });
    
    lazyMarkAsyncItemRead.importAndExecute({ id: conversationId, mailboxInfo });
    lazyOpenBizChatSidePane.importAndExecute({
        source,
        conversationId,
        isNewChat: false,
        shouldExecuteQuery: false,
        targetWindow,
        mailboxInfo,
    });
}
